import React, { useState, useEffect, useRef } from 'react';
import { FiArrowLeft, FiMail, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import { RiCheckboxCircleFill } from 'react-icons/ri';
import { motion } from 'framer-motion';
import { useNavigate } from '@tanstack/react-router';
import { useResetPasswordRequestMutation, useResetPasswordMutation } from '../../api/auth';
import { toast } from '../../utils/toast';
import { extractFieldErrors, getRetryAfter, hasErrorCode } from '../../utils/apiError';
import { AuthHeader } from './AuthHeader';

const checkPasswordStrength = (pw: string) => ({
  isLengthValid: pw.length >= 8 && pw.length <= 20,
  hasUppercase: /[A-Z]/.test(pw),
  hasNumber: /\d/.test(pw),
  hasSpecialChar: /[!#$%&()*+,\-./:;<=>?@[\\\]^_`{|}~]/.test(pw),
});

type ResetStep = 'email' | 'otp' | 'password' | 'success';

export const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<ResetStep>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const pwStrength = checkPasswordStrength(newPassword);
  const passwordIsValid =
    pwStrength.isLengthValid &&
    pwStrength.hasUppercase &&
    pwStrength.hasNumber &&
    pwStrength.hasSpecialChar;

  // OTP Timer State
  const [timeLeft, setTimeLeft] = useState(10 * 60); // 10 minutes
  const [canResend, setCanResend] = useState(false);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Mutations
  const requestReset = useResetPasswordRequestMutation();
  const resetPassword = useResetPasswordMutation();

  // Timer logic for OTP step
  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (step === 'otp' && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [step, timeLeft]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await requestReset.mutateAsync(email);
      setStep('otp');
      setTimeLeft(10 * 60);
      setCanResend(false);
      setEmailError('');
    } catch (err) {
      // Toast is handled by mutation's onError; also show field-level feedback inline
      setEmailError(extractFieldErrors(err).email ?? '');
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) value = value[value.length - 1];
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 4) {
      otpInputRefs.current[index + 1]?.focus();
    }

    // If all digits entered, automatically proceed (optional, but good UX)
    if (newOtp.every((digit) => digit !== '') && newOtp.join('').length === 5) {
      setStep('password');
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 5);
    if (!pasted) return;

    const newOtp = ['', '', '', '', ''];
    pasted.split('').forEach((c, i) => {
      if (i < 5) newOtp[i] = c;
    });
    setOtp(newOtp);

    if (pasted.length === 5) {
      setStep('password');
    } else {
      otpInputRefs.current[pasted.length]?.focus();
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) return;
    try {
      await requestReset.mutateAsync(email);
      setTimeLeft(10 * 60);
      setCanResend(false);
      setOtp(['', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } catch (err) {
      // Toast is handled by mutation's onError; honour the server cooldown if provided
      const retryAfter = getRetryAfter(err);
      if (retryAfter !== undefined) {
        setTimeLeft(retryAfter);
        setCanResend(retryAfter === 0);
      }
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordIsValid) {
      toast.error('Password does not meet requirements.');
      setHasSubmitted(true);
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match.');
      setHasSubmitted(true);
      return;
    }

    try {
      await resetPassword.mutateAsync({
        email,
        otp: otp.join(''),
        newPassword,
      });
      // Success toast is handled in the mutation onSuccess
      setStep('success');
    } catch (err) {
      // Toast is handled by mutation's onError
      if (hasErrorCode(err, 'OTP_EXPIRED') || hasErrorCode(err, 'OTP_MAX_ATTEMPTS')) {
        // The code can no longer be used: send the user back to request/enter a new one
        setOtp(['', '', '', '', '']);
        setStep('otp');
        setTimeLeft(getRetryAfter(err) ?? 0);
        setCanResend(true);
        return;
      }
      setPasswordError(extractFieldErrors(err).new_password ?? '');
    }
  };

  const handleBack = () => {
    if (step === 'otp') setStep('email');
    else if (step === 'password') setStep('otp');
    else window.history.back();
  };

  return (
    <div className="relative min-h-screen w-full flex bg-[#030303] flex-col overflow-hidden font-['Inter'] selection:bg-blue-500/30">
      {/* Background Glow */}
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          width: '1308px',
          height: '449px',
          bottom: '-150px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(to right, #2F80ED, #942FED)',
          opacity: 0.5,
          filter: 'blur(260px)',
          zIndex: 0,
        }}
      />

      <AuthHeader />

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center w-full max-w-[480px] mx-auto px-5 sm:px-6 py-20">
        {/* Back Button */}
        {step !== 'success' && (
          <div className="w-full flex justify-start mb-6">
            <button
              onClick={handleBack}
              className="w-10 h-10 rounded-full bg-[#1a1a1a] hover:bg-[#2a2a2a] flex items-center justify-center transition-colors border border-white/5 cursor-pointer shadow-lg"
            >
              <FiArrowLeft className="text-zinc-300" size={18} />
            </button>
          </div>
        )}

        {step === 'email' && (
          <div className="w-full">
            <div className="w-full text-center mb-8">
              <h1
                className="text-white text-[26px] sm:text-[32px] md:text-[40px] font-medium leading-[1.1] mb-4"
                style={{ fontFamily: "'Funnel Display', sans-serif" }}
              >
                Forgot your password?
              </h1>
              <p className="text-[#a1a1aa] text-[15px] font-medium leading-relaxed max-w-[380px] mx-auto">
                Enter the email address linked to your account. We'll send you a secure code to
                recover your password
              </p>
            </div>

            <form className="w-full flex flex-col gap-6" onSubmit={handleEmailSubmit}>
              <div className="relative flex items-center bg-[#0a0a0a] border border-white/5 rounded-2xl h-[56px] px-5 group focus-within:border-blue-500/50 transition-colors shadow-lg w-full">
                <FiMail className="text-zinc-500 mr-3" size={20} />
                <input
                  type="email"
                  placeholder="Email Address"
                  required
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError('');
                  }}
                  className="bg-transparent border-none text-white text-[16px] placeholder:text-zinc-600 w-full focus:outline-none focus:ring-0 font-medium"
                />
              </div>
              {emailError ? <p className="text-red-400 text-xs -mt-4 ml-1">{emailError}</p> : null}

              <button
                type="submit"
                disabled={requestReset.isPending}
                className="h-[56px] bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold text-[16px] rounded-2xl transition-all shadow-xl shadow-blue-500/20 active:scale-[0.98] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {requestReset.isPending ? 'Sending...' : 'Send verification code'}
              </button>
            </form>
          </div>
        )}

        {step === 'otp' && (
          <div className="w-full">
            <div className="w-full text-center mb-10">
              <h1
                className="text-white text-[26px] sm:text-[32px] font-medium mb-3"
                style={{ fontFamily: "'Funnel Display', sans-serif" }}
              >
                Please check your email
              </h1>
              <p className="text-[#a1a1aa] text-[15px] leading-relaxed">
                We've sent a 5-digit code to{' '}
                <span className="text-blue-400 font-medium">{email}</span>
              </p>
            </div>

            <div className="w-full flex flex-col items-center gap-8">
              <div className="flex gap-3 justify-center">
                {otp.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={(el) => {
                      otpInputRefs.current[idx] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(idx, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                    onPaste={handleOtpPaste}
                    className="w-12 h-12 md:w-14 md:h-14 bg-[#1a1a1a] border border-white/5 rounded-xl text-center text-white text-2xl font-bold focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 transition-all shadow-lg"
                  />
                ))}
              </div>

              <div className="flex flex-col items-center gap-4">
                <div className="text-[14px] font-medium">
                  {timeLeft > 0 ? (
                    <span className="text-zinc-500">
                      Resend in <span className="text-[#3B82F6]">{formatTime(timeLeft)}</span>
                    </span>
                  ) : (
                    <button
                      onClick={handleResendOtp}
                      disabled={requestReset.isPending}
                      className="text-[#3B82F6] hover:text-blue-400 transition-colors font-bold cursor-pointer"
                    >
                      Resend Code
                    </button>
                  )}
                </div>

                <button
                  onClick={() => setStep('email')}
                  className="text-zinc-500 hover:text-white transition-colors text-[14px] font-bold cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 'password' && (
          <div className="w-full">
            <div className="w-full text-center mb-8">
              <h1
                className="text-white text-[26px] sm:text-[32px] md:text-[40px] font-medium leading-[1.1] mb-6"
                style={{ fontFamily: "'Funnel Display', sans-serif" }}
              >
                Create New Password
              </h1>
              <p className="text-[#a1a1aa] text-[15px] font-medium leading-relaxed max-w-[380px] mx-auto">
                Secure your account with a new password
              </p>
            </div>

            <form className="w-full flex flex-col gap-6" onSubmit={handlePasswordSubmit}>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <div className="relative flex items-center bg-[#0a0a0a] border border-white/5 rounded-2xl h-[56px] px-5 group focus-within:border-blue-500/50 transition-colors shadow-lg w-full">
                    <FiLock className="text-zinc-500 mr-3" size={20} />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="New Password"
                      required
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        if (passwordError) setPasswordError('');
                      }}
                      className="bg-transparent border-none text-white text-[16px] placeholder:text-zinc-600 flex-1 focus:outline-none focus:ring-0 font-medium"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-zinc-500 hover:text-zinc-300 transition-colors cursor-pointer shrink-0 ml-2"
                    >
                      {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                    </button>
                  </div>

                  {passwordError ? (
                    <p className="text-red-400 text-xs ml-1">{passwordError}</p>
                  ) : null}

                  {newPassword.length > 0 && (
                    <div className="flex flex-wrap gap-x-3 gap-y-1 ml-1 mt-0.5">
                      {[
                        { label: '8–20 chars', ok: pwStrength.isLengthValid },
                        { label: 'Uppercase', ok: pwStrength.hasUppercase },
                        { label: 'Number', ok: pwStrength.hasNumber },
                        { label: 'Special char', ok: pwStrength.hasSpecialChar },
                      ].map(({ label, ok }) => (
                        <span
                          key={label}
                          className={`text-[11px] font-medium flex items-center gap-1 ${
                            ok ? 'text-green-400' : hasSubmitted ? 'text-red-400' : 'text-zinc-500'
                          }`}
                        >
                          <span>{ok ? '✓' : '○'}</span> {label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <div className="relative flex items-center bg-[#0a0a0a] border border-white/5 rounded-2xl h-[56px] px-5 group focus-within:border-blue-500/50 transition-colors shadow-lg w-full">
                    <FiLock className="text-zinc-500 mr-3" size={20} />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm New Password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="bg-transparent border-none text-white text-[16px] placeholder:text-zinc-600 flex-1 focus:outline-none focus:ring-0 font-medium"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="text-zinc-500 hover:text-zinc-300 transition-colors cursor-pointer shrink-0 ml-2"
                    >
                      {showConfirmPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                    </button>
                  </div>
                  {confirmPassword.length > 0 && (
                    <div className="ml-1 mt-0.5">
                      <span
                        className={`text-[11px] font-medium flex items-center gap-1 ${
                          newPassword === confirmPassword
                            ? 'text-green-400'
                            : hasSubmitted
                              ? 'text-red-400'
                              : 'text-zinc-500'
                        }`}
                      >
                        <span>{newPassword === confirmPassword ? '✓' : '○'}</span> Passwords match
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={resetPassword.isPending}
                className="h-[56px] bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold text-[16px] rounded-2xl transition-all shadow-xl shadow-blue-500/20 active:scale-[0.98] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resetPassword.isPending ? 'Processing...' : 'Set New Password'}
              </button>
            </form>
          </div>
        )}

        {step === 'success' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full text-center py-4"
          >
            <div className="mb-8 flex justify-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  type: 'spring',
                  stiffness: 260,
                  damping: 20,
                  delay: 0.1,
                }}
                className="relative"
              >
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-green-500/20 blur-2xl rounded-full" />

                <div className="relative w-24 h-24 rounded-full bg-green-500/10 flex items-center justify-center text-green-500 border border-green-500/20">
                  <RiCheckboxCircleFill size={64} />
                </div>
              </motion.div>
            </div>
            <h1
              className="text-white text-[26px] sm:text-[32px] md:text-[40px] font-medium leading-[1.1] mb-4"
              style={{ fontFamily: "'Funnel Display', sans-serif" }}
            >
              Password Reset!
            </h1>
            <p className="text-[#a1a1aa] text-[15px] font-medium leading-relaxed max-w-[380px] mx-auto mb-10">
              Your password has been successfully reset. You can now login with your new
              credentials.
            </p>
            <button
              onClick={() => navigate({ to: '/sign-in' })}
              className="w-full h-[56px] bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold text-[16px] rounded-2xl transition-all shadow-xl shadow-blue-500/20 active:scale-[0.98] cursor-pointer"
            >
              Back to Login
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};
